import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ShoppingBag, Sparkles } from "lucide-react";
import EmptyState from "../../components/common/EmptyState";
import Loader from "../../components/common/Loader";
import WishlistItem from "../../components/wishlist/WishlistItem";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";

const optionForCart = (item) => {
  const product = item.product;
  return {
    size: item.selectedSize || product?.sizes?.[1] || product?.sizes?.[0] || "M",
    color: item.selectedColor || product?.colors?.[0] || "",
  };
};

const WishlistPage = () => {
  const { items, loading, pendingKeys, removeItem, updateItemVariant } = useWishlist();
  const { addItem } = useCart();

  const pendingFor = (item) =>
    !!pendingKeys[
      `${String(item.productId || "").trim()}::${String(item.selectedColor || "").trim().toLowerCase()}::${String(item.selectedSize || "").trim().toLowerCase()}`
    ];

  const handleMoveToCart = async (item) => {
    if (!item.product) return;
    const { size, color } = optionForCart(item);
    const added = await addItem(item.product, size, 1, color);
    if (!added) return;
    const removed = await removeItem({
      productId: item.productId,
      selectedColor: item.selectedColor || "",
      selectedSize: item.selectedSize || "",
    });
    if (removed) toast.success("Moved to cart");
  };

  const handleVariantChange = async (item, nextSize, nextColor) => {
    if (!item.product) return;
    await updateItemVariant(item.productId, item.selectedSize || "", item.selectedColor || "", nextSize, nextColor);
  };

  return (
    <div className="min-h-screen bg-cynos-surface px-4 pb-24 pt-20 md:px-8 md:pt-24 lg:px-12" data-testid="wishlist-page">
      <div className="mx-auto max-w-7xl">
        <section className="mb-8 overflow-hidden rounded-[22px] border border-white/10 bg-gradient-to-br from-white/[0.08] via-white/[0.025] to-cynos-red/[0.08] p-5 shadow-[0_24px_80px_rgba(0,0,0,0.28)] md:mb-10 md:p-8">
          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cynos-red/25 bg-cynos-red/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-cynos-red">
                <Heart size={13} fill="currentColor" /> Saved Drops
              </span>
              <h1 className="mt-4 font-anton text-5xl uppercase leading-none text-white md:text-7xl">
                Wishlist.
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base">
                Your private rack of CYNOS picks. Lock the size, pick the color, and move the drop to cart when it feels right.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 md:min-w-[280px] md:gap-3">
              <div className="flex h-10 items-center justify-between rounded-[12px] border border-white/10 bg-black/30 px-3 md:block md:h-auto md:rounded-[16px] md:p-4">
                <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-zinc-500 md:text-[10px] md:tracking-[0.18em]">Saved</p>
                <p className="font-anton text-xl leading-none text-white md:mt-2 md:text-3xl">{items.length}</p>
              </div>
              <Link
                to="/shop"
                className="flex h-10 rounded-[12px] border border-white/10 bg-white px-3 text-black transition hover:bg-cynos-red hover:text-white md:h-auto md:rounded-[16px] md:p-4"
              >
                <span className="m-auto inline-flex items-center gap-1.5 font-mono text-[8px] font-black uppercase tracking-[0.1em] md:mt-auto md:gap-2 md:text-[10px] md:tracking-[0.14em]">
                  Shop More <ShoppingBag size={14} />
                </span>
              </Link>
            </div>
          </div>
        </section>

        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">Curated for checkout</p>
            <h2 className="mt-1 text-lg font-bold uppercase text-white">Saved items</h2>
          </div>
          {items.length > 0 && (
            <span className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 sm:inline-flex">
              <Sparkles size={13} className="text-cynos-red" /> Ready when you are
            </span>
          )}
        </div>

        {loading ? (
          <div className="rounded-[18px] border border-white/10 bg-[#181818] p-10">
            <Loader label="Loading wishlist..." />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-[22px] border border-white/10 bg-[#181818] p-6 shadow-[0_18px_55px_rgba(0,0,0,0.25)]">
            <EmptyState
              title="Your wishlist is empty."
              message="Tap the heart on any drop to save it here."
              action={
                <Link
                  to="/shop"
                  className="inline-flex min-h-[48px] items-center justify-center rounded-[12px] bg-white px-8 py-4 font-mono text-xs font-black uppercase tracking-[0.18em] text-black transition hover:bg-cynos-red hover:text-white"
                >
                  Browse Drops
                </Link>
              }
            />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
              <WishlistItem
                key={`${item.productId}-${item.selectedColor || ""}-${item.selectedSize || ""}`}
                item={item}
                moving={pendingFor(item)}
                onRemove={removeItem}
                onMoveToCart={handleMoveToCart}
                onVariantChange={handleVariantChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
